import React, { useRef, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import _get from 'lodash/get';
import CsvButton from './CsvButton';
import useDataSource from 'Common/Utils/Hooks/useDataSource';
import { convertToDate, formatDate, infoToastAction } from 'utils';
import { formatStatus } from 'Features/AppointeeManagement/Generic/AppointeeResponse/Utils/formatters';

function ExportCsvButton({ dataSource, fileName, includeHeaders = true, onError, schema, text, dynamicColumnFunction }) {
	const link = useRef();
	const exportSchema = useMemo(() => filterSchemaForExport(schema), [schema]);
	const loadedCallback = useCallback(dataLoaded, [exportSchema, includeHeaders]);
	const [{ isLoading }, setDataSource] = useDataSource(null, onError, loadedCallback);
	const globalDispatch = useDispatch();

    function dataLoaded(data) {
		if (!data || !data.length) {
			globalDispatch(infoToastAction('There is nothing to export.'));
			return;
        }

        let revisedExportSchema = filterSchemaForExport(schema);
        if (dynamicColumnFunction) {
            const dynamicColumns = dynamicColumnFunction(data);
            revisedExportSchema.push(...dynamicColumns);
        }

		const csvUri = toCsvDataUri(data, revisedExportSchema, includeHeaders);
		link.current.setAttribute('href', csvUri);
		link.current.click();
	}

	function handleClick(_, e) {
		e.stopPropagation();
		e.preventDefault();

        if (!dataSource || !exportSchema.length) {
			return;
		}

		setDataSource(dataSource);
	}

	return (
		<a ref={link} href="data:text/csv,charset=utf-8," download={fileName || true} tabIndex="-1">
			<CsvButton disabled={isLoading || !dataSource} text={text} onClick={handleClick} />
		</a>
	);
}

function filterSchemaForExport(schema) {
	if (schema && schema.length) {
		return schema.filter(item => item.forExport !== false);
	}

	return [];
}

function formatValue(value, schemaColumn) {
	if (schemaColumn.export && typeof (schemaColumn.export === 'function')) {
		return schemaColumn.export(value);
	}

	if (schemaColumn.type === 'date') {
		return formatDate(convertToDate(value));
	}

	if (schemaColumn.field === 'appointmentStatus') {
		return formatStatus(value);
	}

	return value;
}

function toCsvDataUri(data, schema, includeHeaders) {
	const fields = schema && schema.map(column => column.label || column.field);
	const rows = data.map(dataRow => {
		return schema.reduce((row, schemaColumn) => {
			const value = _get(dataRow, schemaColumn.field);
			row.push(formatValue(value, schemaColumn));
			return row;
		}, []);
	});
    const csv = Papa.unparse({ fields, data: rows }, { header: includeHeaders });
	const csvUri = encodeURI('data:text/csv;charset=utf-8,' + csv);

	return csvUri;
}

const schemaShape = {
	field: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired
};

ExportCsvButton.propTypes = {
	dataSource: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.instanceOf(Promise), PropTypes.func]),
	fileName: PropTypes.string,
	includeHeaders: PropTypes.bool,
	onError: PropTypes.func,
	schema: PropTypes.arrayOf(PropTypes.shape(schemaShape)).isRequired,
    text: PropTypes.string,
    dynamicColumnFunction: PropTypes.func
};

export default ExportCsvButton;
